import React, { FC } from 'react';
import captureException from 'src/common/utils/captureException';

import commonIcons from './common-icons.svg';
import listingIcons from './listing-icons.svg';
import navIcons from './nav-icons.svg';
import searchIcons from './search-icons.svg';
import socialIcons from './social-icons.svg';
import howItWorksIcons from './how-it-works-icons.svg';
import flags from './flag-icons.svg';
import forOwnersIcons from './for-owners-icons.svg';

const CDN_DOMAIN = process.env.NEXT_PUBLIC_ASSETS_CDN;

const imageMap = {
  common: commonIcons.replace(CDN_DOMAIN, ''),
  listing: listingIcons.replace(CDN_DOMAIN, ''),
  nav: navIcons.replace(CDN_DOMAIN, ''),
  search: searchIcons.replace(CDN_DOMAIN, ''),
  social: socialIcons.replace(CDN_DOMAIN, ''),
  'how-it-works': howItWorksIcons.replace(CDN_DOMAIN, ''),
  flags: flags.replace(CDN_DOMAIN, ''),
  forOwners: forOwnersIcons.replace(CDN_DOMAIN, ''),
};

export type IconProps = {
  /**
   * Ids should consist of the `iconSet` and `iconId`.
   * - `iconSet` corresponds to the icon filename's prefix.
   * - `iconId` is the `id` of the symbol in the corresponding file.
   */
  id:
  | `common/${CommonIconIds}`
  | `listing/${ListingIconIds}`
  | `nav/${NavIconsId}`
  | `search/${SearchIconsId}`
  | `social/${SocialIconsId}`
  | `how-it-works/${HowItWorksIconsId}`
  | `flags/${FlagIconsId}`
  | `forOwners/${ForOwnersIconsId}`;
  className: string;
  alt?: string;
};

const Icon: FC<IconProps> = ({ id, className, alt }) => {
  if (!id) {
    captureException(new TypeError('Attempt to render Icon with invalid id'), {
      extra: {
        id,
      },
    });
    return null;
  }

  const [iconSet, iconId] = id.split('/');

  return (
    <svg className={className}>
      {alt && <title>{alt}</title>}
      <use xlinkHref={`${imageMap[iconSet]}#${iconId}`} />
    </svg>
  );
};

export default Icon;

/**
 * Icons that are intended for use on multiple page types
 * Symbols should be added to `public/static-images/icons.svg`
 */
export const commonIconIds = [
  'clear',
  'location-pin',
  'location-arrow',
  'next-arrow',
  'search',
  'support',
  'star',
  'star-half',
  'close',
  'down-arrow',
  'right-arrow',
  'calendar',
  'calendar-up-arrow',
  'calendar-down-arrow',
  'beach-ball',
  'unusual-sighting',
  'looks-good',
  'unavailable',
  '404',
  'global-destinations',
  'more-info',
  'exclamation',
  'arrow-up',
  'plus',
  'minus',
  'heart',
  'binoculars',
  'share',
  'sharelink',
  'sharefacebook',
  'sharetwitter',
  'reserve',
  'male',
  'female',
  'chat',
  'manage',
  'search-mobile-apps',
  'binoculars',
  'plus-circle-fill',
] as const;
type CommonIconIds = typeof commonIconIds[number];

const categorySlugIds = [
  'powerboats',
  'sailboat-rental',
  'events-large-groups',
  'personal-watercraft',
  'houseboat-overnight',
  'tours-lessons',
  'fishing-charters',
  'paddle-sports',
  'cruises-water-taxis-ferries',
] as const;

const boatFeaturesIcons = [
  'airconditioning',
  'fishing-gear',
  'bar',
  'bluetooth',
  'climate-control',
  'food',
  'watersports-equipment',
  'lifejacket',
  'wifi',
] as const;
export type BoatFeaturesIcons = typeof boatFeaturesIcons[number];

/**
 * Icons that are intended for use on the listing pages
 * Symbols should be added to `public/static-images/listing-icons.svg`
 */
export const listingIconIds = [
  ...categorySlugIds.map((slug) => `category-${slug}`),
  ...boatFeaturesIcons,
  'bookings-check-mark',
  'best-of',
  'super-owner-badge',
  'icon-group',
  'captain-icon',
  'model',
  'length',
  'year',
  'listing-map-marker',
  'circle',
  'instabook-stars',
  'instabook-star',
  'with-captain',
  'without-captain',
  'rating-star',
  'superowner',
  'best-of-year',
];
export type ListingIconIds = typeof listingIconIds[number];

/**
 * Icons that are intended for use on the search pages
 * Symbols should be added to `public/static-images/search-icons.svg`
 */
export const searchIconIds = [
  'rounded-prev-button',
  'cluster',
  'map-icon',
  'coins-icon',
  'empty-search',
  'guide',
  'temperature',
  'humidity',
  'pressure',
  'wind',
  'visibility',
  'cloudiness',
  'boat',
  'rent',
  'marina',
  'hotel',
  'dine',
  'fishing',
  'ring-buoy',
  'checklist',
  'map',
  'list',
  'filter',
] as const;
type SearchIconsId = typeof searchIconIds[number];

/**
 * Social icons are used on the footer
 */
export const socialIconIds = [
  'facebook-icon',
  'twitter-icon',
  'instagram-icon',
  'pinterest-icon',
  'linkedin-icon',
  'youtube-icon',
  'tiktok-icon',
  'link-icon',
] as const;
type SocialIconsId = typeof socialIconIds[number];

export const navIconIds = [
  'calendar',
  'inbox',
  'listing',
  'credit-card',
  'decline',
  'chevron',
  'chevron-left',
  'chevron-right',
  'settings',
  'user',
  'help',
] as const;
type NavIconsId = typeof navIconIds[number];

export const flagIconIds = [
  'AE',
  'AR',
  'AU',
  'BR',
  'CA',
  'CH',
  'CN',
  'CR',
  'DK',
  'EU',
  'GB',
  'HK',
  'HR',
  'ID',
  'IL',
  'IN',
  'IS',
  'JP',
  'KE',
  'KR',
  'MA',
  'MX',
  'MY',
  'NO',
  'NZ',
  'PE',
  'PH',
  'PL',
  'RU',
  'SE',
  'SG',
  'TH',
  'TR',
  'TW',
  'US',
  'VN',
  'ZA',
] as const;
export type FlagIconsId = typeof flagIconIds[number];

export const forOwnersIconIds = [
  'boaters',
  'list',
  'step-1',
  'step-2',
  'step-3',
  'step-4',
  'partner-badge',
] as const;
type ForOwnersIconsId = typeof forOwnersIconIds[number];

/**
 * Icons that are intended for use on the How It Works page
 * Symbols should be added to `public/static-images/how-it-works-icons.svg`
 */
export const howItWorksIconIds = ['step-1', 'step-2', 'step-3', 'step-4'] as const;
type HowItWorksIconsId = typeof howItWorksIconIds[number];
